/**
 * Client for the objectives API (apps/api). Same-origin under /api/* on the
 * demo deployment, so requests inherit the site's Basic Auth automatically.
 *
 * The base path can be overridden by the host page setting
 * `window.__OBJETIVOS_API_BASE__` before the bundle loads; defaults to
 * same-origin /api.
 */
declare global {
  interface Window { __OBJETIVOS_API_BASE__?: string }
}
const API_BASE = (typeof window !== 'undefined' && window.__OBJETIVOS_API_BASE__) || '/api';

export const SS27_LINES = ['MAN', 'WOMAN', 'TEEN', 'KIDS', 'BABY', 'NEWBORN'] as const;
export type SS27Line = (typeof SS27_LINES)[number];

/** The three numeric metrics captured per line. */
export interface LineObjective {
  facturacion: number;     // €
  clientes: number;        // expected clients
  clientesNuevos: number;  // new / reactivated clients
}

export type ObjetivosByLine = Record<SS27Line, LineObjective>;

export function emptyObjetivos(): ObjetivosByLine {
  return SS27_LINES.reduce((acc, l) => {
    acc[l] = { facturacion: 0, clientes: 0, clientesNuevos: 0 };
    return acc;
  }, {} as ObjetivosByLine);
}

export interface ObjetivoRow {
  agente_id: string;
  agente_nombre: string;
  linea: string;
  facturacion_eur: string;        // numeric → string from pg
  num_clientes: number;
  num_clientes_nuevos: number;
  updated_at: string;
}

export interface NecesidadRow {
  agente_id: string;
  agente_nombre: string;
  necesidades: string;
  updated_at: string;
}

export interface ObjetivosResponse {
  rows: ObjetivoRow[];
  necesidades: NecesidadRow[];
}

export async function fetchObjetivos(agentId?: string): Promise<ObjetivosResponse> {
  const url = agentId
    ? `${API_BASE}/objetivos-ss27?agentId=${encodeURIComponent(agentId)}`
    : `${API_BASE}/objetivos-ss27`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No se pudieron cargar los objetivos (${res.status})`);
  return (await res.json()) as ObjetivosResponse;
}

export async function saveObjetivos(
  agenteId: string,
  agenteNombre: string,
  objetivos: ObjetivosByLine,
  necesidades: string,
): Promise<void> {
  const res = await fetch(`${API_BASE}/objetivos-ss27`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agenteId, agenteNombre, objetivos, necesidades }),
  });
  if (!res.ok) throw new Error(`No se pudieron guardar los objetivos (${res.status})`);
}

/** Collapse the per-line rows for one agent into a {MAN: {...}, ...} map. */
export function rowsToByLine(rows: ObjetivoRow[]): ObjetivosByLine {
  const out = emptyObjetivos();
  for (const r of rows) {
    if ((SS27_LINES as readonly string[]).includes(r.linea)) {
      out[r.linea as SS27Line] = {
        facturacion: Number(r.facturacion_eur) || 0,
        clientes: Number(r.num_clientes) || 0,
        clientesNuevos: Number(r.num_clientes_nuevos) || 0,
      };
    }
  }
  return out;
}
